import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { SignatureClient } from "@/Common/Api/Signature/SignatureClient";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { FileType } from "@/Common/Models/FileType";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { PinFrequencyDocumentType } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { SignPrintAuthorizationOverlayElement } from "@/ExportableFile/Models/OverlayElements/SignPrintAuthorizationOverlayElement";
import { ExportableFile, IExportableFileLoaded } from "../Models/ExportableFile";
import { ExportableFileResponse } from "../Models/ExportableFileResponse";
import { FileTypeAttachment } from "../Models/FileTypes/FileTypeAttachment";
import { FileTypeConsultationRequest } from "../Models/FileTypes/FileTypeConsultationRequest";
import { FileTypeDocument } from "../Models/FileTypes/FileTypeDocument";
import { FileTypeDocumentBytes } from "../Models/FileTypes/FileTypeDocumentBytes";
import { FileTypeEncounter } from "../Models/FileTypes/FileTypeEncounter";
import { FileTypeExamRequest } from "../Models/FileTypes/FileTypeExamRequest";
import { FileTypeGrowthCurve } from "../Models/FileTypes/FileTypeGrowthCurve";
import { FileTypeImmunizationReport } from "../Models/FileTypes/FileTypeImmunizationReport";
import { FileTypeInvoice } from "../Models/FileTypes/FileTypeInvoice";
import { FileTypeLab } from "../Models/FileTypes/FileTypeLab";
import { FileTypeLabPdf } from "../Models/FileTypes/FileTypeLabPdf";
import { FileTypeMedicalSummary } from "../Models/FileTypes/FileTypeMedicalSummary";
import { FileTypeMedicationSummary } from "../Models/FileTypes/FileTypeMedicationSummary";
import { FileTypeObstetricalFile } from "../Models/FileTypes/FileTypeObstetricalFile";
import { FileTypePatientGrowthChart } from "../Models/FileTypes/FileTypePatientGrowthChart";
import { FileTypeRadPdf } from "../Models/FileTypes/FileTypeRadPdf";
import { FileTypeTemporaryFileForClinicians } from "../Models/FileTypes/FileTypeTemporaryFileForClinicians";
import { FileTypeWorkerClaimCnesst } from "../Models/FileTypes/FileTypeWorkerClaimCnesst";
import { FileTypeWorkSafeBcForm } from "../Models/FileTypes/FileTypeWorkSafeBcForm";
import { PrintFetchUrlResponse } from "../Models/PrintFetchUrlResponse";

class ExportableFileHelper {
    public static fromAttachedObject(model: AttachedObjectResponse): ExportableFile {
        let obj = new ExportableFileResponse({
            Id: model.ObjectId,
            Type: model.Type,
            Description: model.Description,
            LastSignature: model.AssociatedSignedPrintAuthorization
        });

        return this.fromExportableFileResponse(obj);
    }

    public static async mergeExportableFiles(
        filesToMerge: ExportableFile[],
        mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
        onOkAction?: (fileName: string) => void,
        originalFileClientTag?: string,
        isFinalFile: boolean = false,
        exportableFileGenerationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview
    ): Promise<ExportableFile> {
        mutateState?.(s => {
            s.numberOfDocumentsGenerated = 0;
            s.numberOfDocumentsToGenerate = filesToMerge.length + 1;
        });

        let result: IExportableFileLoaded[] = [];
        for (let file of filesToMerge) {
            const generated = await file.getFetchUrlDtoAsync(true, false, false, false, isFinalFile, null, exportableFileGenerationIntent);
            result.push(generated);
            mutateState?.(s => s.numberOfDocumentsGenerated++);
        }

        const request = {
            fetchUrls: result.toHash(p => p.FetchUrl, p => p.OriginalExportableFile.description),
            addErrorPages: false
        };

        if (originalFileClientTag != null) {
            throw "Merge not supported by XCOM";
        }

        const generatedResponse = await Client.postAsync<PrintFetchUrlResponse>(
            ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile,
            request
        );

        mutateState?.(s => {
            s.numberOfDocumentsGenerated = null;
            s.numberOfDocumentsToGenerate = 0;
        });

        return this.validateResponse(generatedResponse, onOkAction)
            ? new FileTypeTemporaryFileForClinicians({ ObjectId: generatedResponse?.data.FileName, description: "", date: "" })
            : null;
    }

    public static async getConcatenatedFetchUrlDtoAsync(
        originalFile: ExportableFile,
        filesToConcatenate: ExportableFile[] = [],
        mutateState?: (mutation: (s: { numberOfDocumentsGenerated: number, numberOfDocumentsToGenerate: number }) => void) => void,
        onOkAction?: () => void,
        isFinalFile: boolean = false,
        exportableFileGenerationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview
    ): Promise<ExportableFile> {
        if (!filesToConcatenate?.any()) {
            mutateState?.(s => {
                s.numberOfDocumentsGenerated = 0;
                s.numberOfDocumentsToGenerate = null;
            });
            return originalFile;
        }

        return await this.mergeExportableFiles(
            [originalFile, ...filesToConcatenate],
            mutateState,
            onOkAction,
            originalFile.clientTag,
            isFinalFile,
            exportableFileGenerationIntent
        );
    }

    private static validateResponse(response: ClientResponse<PrintFetchUrlResponse>, onOkAction: (fileName: string) => void): boolean {
        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        if (!response.data.HasContent) {
            sessionStore.showPopup(cacheStore.GetString("PrintOrFax_AllUnprintable"));
            return false;
        }

        if (response.data.FailedFiles?.any()) {
            sessionStore.showPopup({
                content: [`${cacheStore.GetString("PrintOrFax_UnsupportedFileTypes")}:`].concat(response.data.FailedFiles),
                autoWidth: true,
                contentStyle: "list-after-first",
                okAction: () => onOkAction?.(response.data.FileName)
            });
            return false;
        }

        return true;
    }

    public static fromExportableFileResponse(file: ExportableFileResponse): ExportableFile {
        let exportableFile: ExportableFile;

        switch (file.Type) {
            case FileType.LAB:
                exportableFile = new FileTypeLab({
                    LabfillerId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.ENCOUNTER:
                exportableFile = new FileTypeEncounter({
                    EncounterId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.DOCUMENT:
                exportableFile = new FileTypeDocument({
                    DocumentId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.MEDICALSUMMARY:
                exportableFile = new FileTypeMedicalSummary(file.Id);
                break;
            case FileType.MEDICATIONSUMMARY:
                exportableFile = new FileTypeMedicationSummary(file.Id);
                break;
            case FileType.HEIGHTGRAPH:
            case FileType.WEIGHTGRAPH0TO2:
            case FileType.WEIGHTGRAPH2PLUS:
            case FileType.BMIGRAPH:
            case FileType.HEADCIRCUMFERENCEGRAPH:
            case FileType.LENGTHGRAPH:
                exportableFile = new FileTypeGrowthCurve({
                    PatientContactId: file.Id,
                    description: file.Description,
                    date: DateTime.now(),
                    fileType: file.Type
                });
                break;
            case FileType.RADPDF:
                exportableFile = new FileTypeRadPdf({
                    patientFormId: file.Id,
                    description: file.Description,
                    date: file.Date,
                    formId: null
                });
                break;
            case FileType.LABPDF:
                exportableFile = new FileTypeLabPdf({
                    EncapsulatedDataId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.EXAMREQUEST:
                exportableFile = new FileTypeExamRequest({
                    ExamRequestId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.CONSULTATIONREQUEST:
                exportableFile = new FileTypeConsultationRequest({
                    ConsultationRequestId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.WORKERCLAIM:
                exportableFile = new FileTypeWorkerClaimCnesst({
                    workerClaimId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.ATTACHMENT:
                exportableFile = new FileTypeAttachment({
                    AttachmentId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.DOCUMENTBYTES:
                exportableFile = new FileTypeDocumentBytes({
                    DocumentBytesId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.OBSTETRICALFILE:
                exportableFile = new FileTypeObstetricalFile({
                    contributionId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.WorkSafeBcForm:
                exportableFile = new FileTypeWorkSafeBcForm({
                    formId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.PatientGrowthChart:
                exportableFile = new FileTypePatientGrowthChart({
                    chartId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.Invoice:
                exportableFile = new FileTypeInvoice({
                    privateBillId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;
            case FileType.ImmunizationReport:
                exportableFile = new FileTypeImmunizationReport({
                    patientContactId: file.Id,
                    description: file.Description,
                    date: file.Date
                });
                break;

            default:
                throw "Not Implemented Type";
        }

        exportableFile.isExternal = file.IsExternal;
        exportableFile.clientTag = file.ClientTag;
        exportableFile.claimId = file.ClaimId;

        if (file.LastSignature) {
            exportableFile.applyOverlayElement(new SignPrintAuthorizationOverlayElement({
                SignedPrintAuthorization: file.LastSignature
            }));
        }
        return exportableFile;
    }

    /**
     * Get the absolute server file path to a file with all the given documents merged.
     * @param files The list of files to merge.
     * @param addErrorPages Whether to add error pages to the document if any of the files couldn't be added.
     */
    public static async mergeMultipleFilesIntoSingleOneAsync(files: ExportableFile[], addErrorPages: boolean): Promise<ClientResponse<PrintFetchUrlResponse>> {
        const loadedFiles = await Promise.all(
            files.map(file => file.getFetchUrlDtoAsync())
        );

        return await Client.postAsync<PrintFetchUrlResponse>(
            ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile,
            {
                fetchUrls: loadedFiles.toHash(fileKey => fileKey.FetchUrl, fileValue => fileValue.OriginalExportableFile.description),
                addErrorPages: addErrorPages
            }
        );
    }

    /**
     * Get the PIN frequency token from a model for an exportable file type.
     * @param fileType The exportable file type.
     * @param loginPinFrequencyModel The login PIN frequency model to read.
     */
    public static getPinFrequencyTokenByFileType(fileType: FileType, loginPinFrequencyModel: LoginPinFrequencyModel): string {
        if (!loginPinFrequencyModel)
            return null;

        switch (fileType) {
            case FileType.CONSULTATIONREQUEST:
                return loginPinFrequencyModel.consultationRequestToken;

            case FileType.DOCUMENT:
                return loginPinFrequencyModel.documentToken;

            case FileType.EXAMREQUEST:
                return loginPinFrequencyModel.examRequestToken;

            case FileType.RADPDF:
                return loginPinFrequencyModel.formToken;

            case FileType.Prescription:
                return loginPinFrequencyModel.prescriptionToken;

            case FileType.OBSTETRICALFILE:
                return loginPinFrequencyModel.obstetricalFileToken;

            default:
                return null;
        }
    }

    /**
     * Get the PIN frequency type from a model for an exportable file type.
     * @param fileType The exportable file type.
     */
    public static getPinFrequencyDocumentTypeByFileType(fileType: FileType): PinFrequencyDocumentType {
        switch (fileType) {
            case FileType.CONSULTATIONREQUEST:
                return PinFrequencyDocumentType.ConsultationRequest;

            case FileType.DOCUMENT:
                return PinFrequencyDocumentType.Document;

            case FileType.EXAMREQUEST:
                return PinFrequencyDocumentType.ExamRequest;

            case FileType.RADPDF:
                return PinFrequencyDocumentType.Form;

            case FileType.Prescription:
                return PinFrequencyDocumentType.Prescription;

            case FileType.OBSTETRICALFILE:
                return PinFrequencyDocumentType.ObstetricalFile;

            default:
                return null;
        }
    }

    /**
     * Validates the pin frequency token.
     * @param token The pin frequency token.
     */
    public static async validatePinFrequencyToken(token: string): Promise<boolean> {
        return (
            token
            && await SignatureClient.validatePinFrequencyToken(token)
        ) ?? false;
    }

    /**
     * lock medical request when no need to use their pin
     * @param requestId lab or consultation request Id
     * @param medicalRequestType lab or consultation request type
     */
    public static async lockMedicalRequest(requestId: string, medicalRequestType: MedicalRequestType): Promise<void> {
        await Client.postAsync<any>(ServerOperations.Server.Myle_MedicalRequest_MarkAsPrinted, {
            MedicalRequestId: requestId,
            MedicalRequestType: medicalRequestType
        });
    }
}

export { ExportableFileHelper };
